import * as React from 'react'
import { Box, Button, Typography, Stack } from '@mui/material'
import { useAddStandards, useUpdateStandards } from './hooks'
import { ContentStandardsChangelistResp } from './types'

interface ApplyChangelistProps {
  changelist: ContentStandardsChangelistResp
}

export const ApplyChangelist: React.FC<ApplyChangelistProps> = ({
  changelist,
}) => {
  const { addStandards, loadingAddStandards } = useAddStandards()
  const { updateStandards, loadingUpdateStandards } = useUpdateStandards()

  const [isApplying, setIsApplying] = React.useState(false)
  const [resultMessage, setResultMessage] = React.useState<string | null>(null)

  const handleApply = React.useCallback(async () => {
    if (changelist?.data?.changelist) {
      setIsApplying(true)
      setResultMessage(null)

      const { adds, updates } = changelist.data.changelist
      const messages = []

      try {
        // Apply adds if any
        if (adds && adds.length > 0) {
          const addSuccess = await addStandards(adds)
          if (addSuccess) {
            messages.push(`Successfully added ${adds.length} standards`)
          } else {
            messages.push(`Error adding standards`)
          }
        }

        // Apply updates if any
        if (updates && updates.length > 0) {
          // Convert KiddomUpdateStandardFull to KiddomUpdateStandard
          const updateData = updates.map((update) => ({
            standard_id: update.standard_id,
            state: update.state,
            standard_name: update.standard_name,
            standard_description: update.standard_description,
            adoption_date: update.adoption_date,
          }))

          const updateSuccess = await updateStandards(updateData)
          if (updateSuccess) {
            messages.push(`Successfully updated ${updates.length} standards`)
          } else {
            messages.push(`Error updating standards`)
          }
        }

        setResultMessage(messages.join('. '))
      } catch (error) {
        setResultMessage(`Error applying changes: ${error}`)
      } finally {
        setIsApplying(false)
      }
    }
  }, [changelist, addStandards, updateStandards])

  // Check if changelist has data
  if (!changelist?.data?.changelist) {
    return null
  }

  const { adds, updates } = changelist.data.changelist
  const hasAdds = adds && adds.length > 0
  const hasUpdates = updates && updates.length > 0

  if (!hasAdds && !hasUpdates) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No changes to apply
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6" gutterBottom>
        Changes to Apply
      </Typography>

      <Stack spacing={1}>
        <Typography variant="body2" color="text.secondary">
          Adds: {adds?.length || 0}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Updates: {updates?.length || 0}
        </Typography>
      </Stack>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleApply}
          disabled={
            isApplying ||
            loadingAddStandards ||
            loadingUpdateStandards ||
            !!resultMessage
          }
          sx={{ textTransform: 'none' }}
        >
          {isApplying ? 'Applying...' : 'Apply Changes'}
        </Button>
        {resultMessage && (
          <Typography
            variant="body2"
            color={resultMessage.includes('Error') ? 'error' : 'success'}
          >
            {resultMessage}
          </Typography>
        )}
      </Box>
    </Box>
  )
}
